import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetHallMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    hall_name: '',
    max_capacity: 100,
    default_capacity: 100,
    is_combinable: false,
    is_active: true
  });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadRows = async () => {
    if (!chainId || !propertyId) return;
    const res = await fetch('/api/v1/banquet/halls', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load halls');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    loadRows().catch((err) => setError(err?.message || 'Failed to load halls'));
  }, [chainId, propertyId, outletId]);

  const save = async () => {
    setError('');
    if (!form.hall_name || Number(form.max_capacity || 0) <= 0) {
      return setError('hall_name and max_capacity are required');
    }
    const url = editingId ? `/api/v1/banquet/halls/${editingId}` : '/api/v1/banquet/halls';
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        hall_name: form.hall_name,
        max_capacity: Number(form.max_capacity),
        default_capacity: Number(form.default_capacity || form.max_capacity),
        is_combinable: form.is_combinable === true,
        is_active: form.is_active !== false
      })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    setEditingId('');
    setForm({ hall_name: '', max_capacity: 100, default_capacity: 100, is_combinable: false, is_active: true });
    loadRows();
  };

  const edit = (r) => {
    setEditingId(String(r.hall_id));
    setForm({
      hall_name: r.hall_name || '',
      max_capacity: Number(r.max_capacity || 0),
      default_capacity: Number(r.default_capacity || 0),
      is_combinable: r.is_combinable === true,
      is_active: r.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (hallId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/halls/${hallId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadRows();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Hall Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <input placeholder="Hall name" value={form.hall_name} onChange={(e) => setForm({ ...form, hall_name: e.target.value })} />
        <input placeholder="Max capacity" type="number" min="1" value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: e.target.value })} />
        <input placeholder="Default capacity" type="number" min="1" value={form.default_capacity} onChange={(e) => setForm({ ...form, default_capacity: e.target.value })} />
        <label><input type="checkbox" checked={form.is_combinable} onChange={(e) => setForm({ ...form, is_combinable: e.target.checked })} /> Combinable</label>
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Hall' : 'Add Hall'}</button>
        {editingId ? <button onClick={() => { setEditingId(''); setForm({ hall_name: '', max_capacity: 100, default_capacity: 100, is_combinable: false, is_active: true }); }}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Hall</th>
              <th style={{ textAlign: 'right' }}>Max</th>
              <th style={{ textAlign: 'right' }}>Default</th>
              <th style={{ textAlign: 'left' }}>Combinable</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.hall_id}>
                <td>{r.hall_name}</td>
                <td style={{ textAlign: 'right' }}>{r.max_capacity}</td>
                <td style={{ textAlign: 'right' }}>{r.default_capacity || '-'}</td>
                <td>{r.is_combinable ? 'Yes' : 'No'}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.hall_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={6}>No halls found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetHallMaster;
